import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import { FIREBASE_COLLECTIONS } from "../config/firebaseCollections";
import {
  resolveLinkedClassIds,
  syncLinkedContentBoxActiveDates,
  uniqueClassIds,
} from "utils/linkedContentBoxes";

export const LINKED_ACTIVE_DATES_SCOPE_EVENT = "lesson-launchpad:linked-active-dates-scope";
export const LINKED_ACTIVE_DATES_SCOPE_ALL = "all-linked";
export const LINKED_ACTIVE_DATES_SCOPE_CURRENT = "current-class";
export const LINKED_ACTIVE_DATES_SCOPE_CANCELLED = "cancelled";

export const ACTIVE_DATES_LINKED_SCOPE_SAVE_ERROR =
  "Unable to save active dates for linked boxes. Please try again.";

const buildContentBoxRef = (userId, classId, contentBoxId) =>
  doc(
    db,
    FIREBASE_COLLECTIONS.USERS,
    userId,
    FIREBASE_COLLECTIONS.CLASSES,
    classId,
    FIREBASE_COLLECTIONS.CONTENT_BOXES,
    contentBoxId
  );

export function requestLinkedActiveDatesScope(request = {}) {
  if (typeof window === "undefined" || typeof window.dispatchEvent !== "function") {
    return Promise.resolve({
      scope: LINKED_ACTIVE_DATES_SCOPE_CURRENT,
      scopeClassIds: [request.sourceClassId].filter(Boolean),
    });
  }

  return new Promise((resolve) => {
    const event = new CustomEvent(LINKED_ACTIVE_DATES_SCOPE_EVENT, {
      cancelable: true,
      detail: {
        ...request,
        resolve,
      },
    });
    window.dispatchEvent(event);
    if (!event.defaultPrevented) {
      resolve({
        scope: LINKED_ACTIVE_DATES_SCOPE_CURRENT,
        scopeClassIds: [request.sourceClassId].filter(Boolean),
      });
    }
  });
}

export async function resolveLinkedActiveDateScopeClassIds({
  userId,
  sourceClassId,
  contentBoxId,
  linkedClassIds,
  classIds = [],
}) {
  const resolvedIds = await resolveLinkedClassIds(userId, contentBoxId, {
    sourceClassId,
    classIds,
    hintLinkedClassIds: linkedClassIds,
  });

  return uniqueClassIds([sourceClassId, ...resolvedIds]);
}

export async function saveContentBoxActiveDatesToScope({
  userId,
  classId,
  contentBoxId,
  activeDates,
  scopeClassIds,
  classes = [],
}) {
  const normalizedScopeClassIds = uniqueClassIds(scopeClassIds?.length ? scopeClassIds : [classId]);
  const datePayload = {
    startDate: activeDates.startDate,
    endDate: activeDates.endDate,
  };

  if (!normalizedScopeClassIds.length) {
    return { ok: false, linkedClassIds: [], failedClassIds: [], error: ACTIVE_DATES_LINKED_SCOPE_SAVE_ERROR };
  }

  if (normalizedScopeClassIds.length === 1 && normalizedScopeClassIds[0] === String(classId)) {
    await setDoc(buildContentBoxRef(userId, classId, contentBoxId), datePayload, { merge: true });
    return { ok: true, linkedClassIds: [String(classId)], failedClassIds: [] };
  }

  return syncLinkedContentBoxActiveDates(userId, contentBoxId, datePayload, {
    sourceClassId: classId,
    targetClassIds: normalizedScopeClassIds,
    classes,
  });
}

export async function maybePromptForLinkedActiveDatesScope({
  userId,
  classId,
  contentBoxId,
  contentBox,
  activeDates,
  classes = [],
  actionLabel = "change active dates",
  scopeClassIds,
}) {
  if (scopeClassIds?.length) {
    return {
      scope: scopeClassIds.length > 1 ? LINKED_ACTIVE_DATES_SCOPE_ALL : LINKED_ACTIVE_DATES_SCOPE_CURRENT,
      scopeClassIds: uniqueClassIds(scopeClassIds),
    };
  }

  let resolvedContentBox = contentBox;
  if (!resolvedContentBox && userId && classId && contentBoxId) {
    const contentBoxSnap = await getDoc(buildContentBoxRef(userId, classId, contentBoxId));
    resolvedContentBox = contentBoxSnap.exists() ? contentBoxSnap.data() : null;
  }

  if (!resolvedContentBox?.linkedBox) {
    return {
      scope: LINKED_ACTIVE_DATES_SCOPE_CURRENT,
      scopeClassIds: [String(classId)],
    };
  }

  const linkedScopeClassIds = await resolveLinkedActiveDateScopeClassIds({
    userId,
    sourceClassId: classId,
    contentBoxId,
    linkedClassIds: resolvedContentBox.linkedClassIds,
    classIds: (classes || []).map((item) => item?.classId).filter(Boolean),
  });

  if (linkedScopeClassIds.length <= 1) {
    return {
      scope: LINKED_ACTIVE_DATES_SCOPE_CURRENT,
      scopeClassIds: [String(classId)],
    };
  }

  const choice = await requestLinkedActiveDatesScope({
    userId,
    sourceClassId: classId,
    contentBoxId,
    contentBox: {
      ...resolvedContentBox,
      contentBoxId,
    },
    activeDates,
    classes,
    linkedClassIds: linkedScopeClassIds,
    actionLabel,
  });

  return choice || {
    scope: LINKED_ACTIVE_DATES_SCOPE_CANCELLED,
    scopeClassIds: [],
  };
}

export async function saveContentBoxActiveDatesWithLinkedScope({
  userId,
  classId,
  contentBoxId,
  activeDates,
  contentBox,
  classes = [],
  actionLabel,
  scopeClassIds,
}) {
  const scopeChoice = await maybePromptForLinkedActiveDatesScope({
    userId,
    classId,
    contentBoxId,
    contentBox,
    activeDates,
    classes,
    actionLabel,
    scopeClassIds,
  });

  if (scopeChoice?.scope === LINKED_ACTIVE_DATES_SCOPE_CANCELLED) {
    return { ok: false, cancelled: true, linkedClassIds: [], failedClassIds: [] };
  }

  const saveResult = await saveContentBoxActiveDatesToScope({
    userId,
    classId,
    contentBoxId,
    activeDates,
    scopeClassIds: scopeChoice.scopeClassIds,
    classes,
  });

  return {
    ...saveResult,
    scope: scopeChoice.scope,
    scopeClassIds: uniqueClassIds(scopeChoice.scopeClassIds),
  };
}
